import { faqsPl } from "@data/ai-integration-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mobileClusterLinks,
  mobileKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mobile-web-landing/shared";
import {
  Bot,
  Calendar,
  CheckCircle,
  Code,
  FileText,
  LifeBuoy,
  MessageSquare,
  PenTool,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Integracja AI w aplikacji - chatboty, LLM i automatyzacja";

export const description =
  "Integracja AI w aplikacji: chatbot AI, OpenAI API, LLM i automatyzacja. Polski software house — wycena wdrożenia, koszty API i zgodność z RODO.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Integracja AI w aplikacji",
    description,
    "AI integration and LLM chatbot development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Integracja AI",
  headerTail: "w aplikacji",
  description:
    "Wdrażamy chatboty, LLM i automatyzację w aplikacjach mobilnych, webowych i SaaS. OpenAI API i agenci pod realny efekt biznesowy — z kosztami wdrożenia, użycia API i RODO na stole.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Chatbot i OpenAI API", Icon: CheckCircle },
    { text: "Koszt wdrożenia + API", Icon: CheckCircle },
    { text: "RODO i dane w UE", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest integracja AI w aplikacji?",
    p: "AI to zwykle cecha produktu — chatbot w sklepie, MVP z OpenAI, agent przy CRM — a nie osobny „projekt AI od zera”.",
  },
  points: [
    {
      Icon: MessageSquare,
      title: "Chcesz chatbot AI do sklepu lub supportu",
      description:
        "Status zamówienia, rekomendacje produktów, wielojęzyczność — podobnie jak w popularnych narzędziach obsługi klienta, ale dopasowane do Waszego procesu.",
    },
    {
      Icon: Sparkles,
      title: "Dokładasz OpenAI / LLM do istniejącej aplikacji",
      description:
        "Mobile, web lub PWA już działa — potrzebujesz sensownej integracji API, historii rozmów i kontroli kosztów.",
    },
    {
      Icon: Bot,
      title: "Budujesz agenta AI z CRM i bazami danych",
      description:
        "Nie tylko chat: działania w systemie (zgłoszenie, ticket, dane klienta) przy jasnych regułach i audycie.",
    },
    {
      Icon: ShieldCheck,
      title: "Zależy Ci na RODO / GDPR i danych firmowych",
      description:
        "Hosting, retencja, zgody i ograniczenie kontekstu modelu — szczególnie przy danych klientów w UE.",
    },
    {
      Icon: Rocket,
      title: "Chcesz wąskie MVP AI w kilka tygodni / miesięcy",
      description:
        "Jeden konkretny scenariusz, mierzalny efekt, bez budowy pełnej platformy obsługi na start.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opisz scenariusz AI (chatbot, rekomendacje, agent) — zaproponujemy zakres MVP i rozbicie kosztów.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto integrować AI z nami?",
    p: "AI ma sens, gdy widać efekt: mniej ticketów, lepsze rekomendacje, szybsza obsługa. Pokazujemy koszt developmentu i koszt użycia API — bez obiecywania magii „AI-first”.",
    imageAlt: "Integracja AI w aplikacji — chatboty i LLM",
  },
  values: [
    {
      Icon: MessageSquare,
      title: "Chatbot pod realne scenariusze",
      description:
        "FAQ, status zamówienia, kwalifikacja zapytań — zaprojektowany pod Wasz katalog i procesy, nie generyczny widget.",
    },
    {
      Icon: Workflow,
      title: "Integracja z istniejącym stackiem",
      description:
        "Podłączamy LLM do aplikacji, CRM, bazy i paneli — AI jako warstwa, nie osobny silos.",
    },
    {
      Icon: CheckCircle,
      title: "Transparentne koszty utrzymania",
      description:
        "Wycena obejmuje development i orientacyjne koszty API przy zakładanym ruchu.",
    },
    {
      Icon: ShieldCheck,
      title: "RODO i bezpieczeństwo danych",
      description:
        "Kontrola tego, co trafia do modelu, retencja, dane w UE i ograniczenie wrażliwych informacji.",
    },
    {
      Icon: Rocket,
      title: "MVP zamiast platformy od razu",
      description:
        "Zaczynamy od wąskiego scenariusza — potem skalujemy do pełniejszego rozwiązania, jeśli wyniki się bronią.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Integracja AI — co wdrażamy",
    p: "Od chatbota w e-commerce po agenta przy CRM. Zakres łączymy z aplikacją mobilną, webową lub SaaS.",
  },
  services: [
    {
      Icon: Search,
      title: "Dobór scenariusza i ocena opłacalności",
      description:
        "Który scenariusz AI daje zwrot? Szacujemy development, koszty API i ryzyka compliance przed buildem.",
    },
    {
      Icon: MessageSquare,
      title: "Chatbot AI w aplikacji lub na stronie",
      description:
        "Widget / in-app chat, baza wiedzy, wielojęzyczność i handoff do człowieka.",
    },
    {
      Icon: Sparkles,
      title: "Integracja OpenAI / LLM API",
      description:
        "Podłączenie modelu do mobile, web lub PWA — historia rozmów, moderacja, limity kosztów.",
    },
    {
      Icon: Bot,
      title: "Agenci i automatyzacja (CRM, n8n…)",
      description:
        "Akcje w systemach zewnętrznych, workflow i audyt decyzji agenta.",
    },
    {
      Icon: ShieldCheck,
      title: "RODO, retencja i bezpieczeństwo",
      description:
        "Architektura pod dane firmowe: zgody, logi, izolacja kontekstów i hosting w UE.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie jakości odpowiedzi",
      description:
        "Monitoring jakości odpowiedzi, aktualizacja bazy wiedzy i rozwój po starcie — AI wymaga opieki jak każdy produkt.",
    },
  ],
  cta: {
    h4: "Nie wiesz, czy AI ma sens w Twojej aplikacji?",
    p: "Porozmawiajmy o jednym konkretnym scenariuszu — dostaniesz uczciwą ocenę kosztów i ryzyka.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda wdrożenie AI w aplikacji",
    p: "Od wyboru scenariusza po produkcję z kontrolą kosztów API. Krótki cykl walidacji zamiast wielomiesięcznego „projektu AI”.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "Omawiamy cel biznesowy, dane wejściowe i czy startujemy od chatbota, agenta czy rekomendacji.",
    },
    {
      Icon: FileText,
      title: "Scenariusz i szacunek kosztów",
      duration: "w kilka dni",
      description:
        "Zakres pierwszego wydania, ryzyka RODO oraz rozbicie: development vs orientacyjne koszty API.",
    },
    {
      Icon: PenTool,
      title: "Projekt rozmowy / workflow",
      duration: "ustalenie harmonogramu",
      description:
        "Scenariusze, ton, handoff do człowieka, integracje z CRM lub katalogiem.",
    },
    {
      Icon: Code,
      title: "Implementacja i integracja",
      duration: "min. 3–8 tygodni",
      description:
        "Podłączamy LLM do aplikacji, budujemy UI chatu / API i limity bezpieczeństwa.",
    },
    {
      Icon: Search,
      title: "Testy jakości i compliance",
      duration: "1–2 tygodnie",
      description:
        "Sprawdzamy odpowiedzi, edge case'y, retencję danych i koszty przy ruchu testowym.",
    },
    {
      Icon: CheckCircle,
      title: "Produkcja i utrzymanie",
      duration: "na stałe",
      description:
        "Monitoring kosztów API, poprawki jakości odpowiedzi i rozwój kolejnych scenariuszy AI.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Wyceń integrację AI",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski zespół integracji AI w aplikacjach",
  imageAlt: "Zespół Blues Brackets — integracja AI w aplikacji",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o integrację AI",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz dodać AI do aplikacji?",
    p: "Umów się na darmową konsultację — opowiedz o chatbocie, OpenAI lub agencie, a zaproponujemy zakres MVP i koszty.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Integracja AI w aplikacji — chatboty, LLM i automatyzacja. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/aplikacje-mobilne-i-webowe",
        text: "Aplikacje mobilne i webowe",
      },
      ...mobileClusterLinks.filter(
        l =>
          l.href !== "/aplikacje-mobilne-i-webowe" &&
          l.href !== "/integracja-ai-w-aplikacji",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mobileKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
